import Modal from "react-responsive-modal";
import { useAuth, useFlagReview, useI18N } from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData, VenueDetails, VenueReview } from "common/types";
import Zoom from "react-medium-image-zoom";

import AnalyticsService from "@/services/analytics";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useDarkMode } from "@/hooks/useDarkMode";
import WideButton from "@/components/common/WideButton";
import Loader from "@/components/common/Loader";
import { Storage } from "common/storage";

type FlagVenueReviewProps = {
  review: VenueReview;
  venueName: string;
  isOpen: boolean;
  onClose: () => void;
  venueDetails: VenueDetails;
};

function FlagVenueReview({
  review,
  venueName,
  isOpen,
  onClose,
  venueDetails,
}: FlagVenueReviewProps) {
  const i18n = useI18N();

  const { user } = useAuth();

  const showToast = useCustomToast();

  const [note, setNote] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isDark = useDarkMode();
  const { mutate, error, isPending, data } = useFlagReview();

  useEffect(() => {
    if (error) {
      console.log("useFlagPhoto", error);

      AnalyticsService.trackGAEventHandler("flagReviewReportError");
      AnalyticsService.trackMx("flagReviewReportError", {
        VenueId: venueDetails.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueTypes: venueDetails.types,
      });

      showToast(error.message, "error");
    }
  }, [error]);

  useEffect(() => {
    if (data) {
      console.log("useFlagPhoto", data);
      showToast(data.message, "success");
      setAttachedImages([]);
      setNote("");
      onClose();
    }
  }, [data]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (note.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    setErrorMessage(null);

    // TRACKING

    AnalyticsService.trackGAEventHandler("flagReviewReportCreate");
    AnalyticsService.trackMx("flagReviewReportCreate", {
      VenueId: venueDetails.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.types,
      ReviewerName: user?.name,
    });

    mutate({
      reviewId: review.id.toString(),
      accessToken: Storage.getCurrentUserToken(),
      notes: note,
      images: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={isOpen}
      onClose={() => onClose()}
      classNames={{
        modal:
          "bg-[transparent] p-0 focus:outline-0 shadow-none w-[45vh] md:w-[70vh]",
        closeButton: "hidden",
      }}
    >
      {isPending && <Loader />}
      <section className="flex flex-col bg-white dark:bg-primary-900 rounded-xl h-[calc(100vh-150px)] md:h-[calc(100vh-220px)] overflow-y-auto"
      role="dialog" aria-label={i18n.get("flagReview")}  tabIndex={0} ref={firstFocusRef}
      >
        <header className="py-4 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 vsticky top-0">
          <div
            className="desktop-h1-xxsmall-bold text-center text-primary-800 dark:text-primary-150"
            // tabIndex={0}
            // ref={firstFocusRef} // Attach the ref here
          >
            {i18n.get("flagReview")}
          </div>
        </header>

        <div
          aria-label="Modal Content"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-primary-500 dark:!text-primary-300 paragraph-xsmall-semibold">
                  {venueName}
                </div>
                <h1 className="self-stretch text-primary-800 dark:text-primary-150 desktop-h4-medium-bold">
                  {i18n.get("helpUsCatchEveryError")}
                </h1>
              </div>
              <div className="self-stretch text-primary-800 dark:text-primary-150 paragraph-small-semibold md:desktop-h4-medium-semi-bold pt-1">
                {i18n.get("haveYouSpottedSomethingWrong")}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className=" w-full ">
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhatIsIncorrect")}
                  className="scrollbar-thin dark:scrollbar-thin-dark h-28"
                  value={note}
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNote(e.target.value);
                  }}
                  maxLength={500}
                  ref={noteRef}
                />
                <div className="button-small-medium text-neutral-500 mt-3">
                  <span className={note.length < 5 ? "text-red-600" : ""}>
                    {note.length}
                  </span>
                  /500 Characters - at least 5 characters required
                </div>
                {errorMessage && (
                  <div className="text-red-600">{errorMessage}</div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto">
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt="attached image"
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <button
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index
                            )
                          );
                        }}
                        className="absolute top-2 right-3 m-2"
                      >
                        <div className="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex">
                          <div className="w-4 h-4 relative flex-col justify-start items-start flex">
                            <img
                              src="/images/Close.svg"
                              alt="Close"
                              className="w-4 h-4"
                            />
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer className="py-3 min-h-14 flex items-center justify-center text-center px-6 border-b border-primary-200 dark:border-primary-150 sticky bottom-0 bg-white dark:bg-primary-900  z-1000">
          <div className="flex-col justify-end items-center gap-4 flex  w-[90%] md:w-full md:max-w-[70%]">
            <div className="w-[327px] justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture" // Wrap the entire div with the label
                className="grow shrink basis-0 h-12 px-6 py-3  bg-primary-50 dark:bg-dark-800 rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
              >
                <div className="text-center text-primary-800 dark:text-primary-150 mobile-h3-small-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-[327px] justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={note.length < 5}
                onClick={() => {
                  submit();
                }}
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
        <button
          onClick={() => {
            setAttachedImages([]);
            setNote("");
            onClose();
          }}
          className="absolute top-4 left-4"
          aria-label="Close modal"
        >
          <XIcon color={isDark ? "#A39FE8" : "#352FC3"} />
        </button>
      </section>
    </Modal>
  );
}

export default FlagVenueReview;
